import React, { useEffect, useRef, useState } from "react";
import Text from "components/FormElements/Text";
import { useAuth } from "hooks/Auth";
import { Link } from "react-router-dom";
import SecondaryButton from "components/Buttons/SecondaryButton";
import { oauthGet, oauthPut } from "helpers/Request";
import { Formik, Form } from "formik";
import AutocompleteInput from "components/FormElements/AutocompleteInput";
import { transform as roleTransformer } from "schemas/Role";
import { transform as userTransformer } from "schemas/User";
import { useNotification } from "hooks/Notification";
import { useModal } from "hooks/Modal";

export default function RolesTable(props) {
    const auth = useAuth();
    const modal = useModal();
    const notification = useNotification();
    const [roles, setRoles] = useState(() => props.user?.roles || []);
    const formRef = useRef();

    const addGroups = (values) => {
        const newRoles = values.roles.map((r) => r.value);

        return oauthPut(`/api/v2/user/${props.user.uuid}`, {
            roles: newRoles,
            append_roles: true,
        })
            .then((data) => {
                setRoles(userTransformer(data).roles);
            })
            .then(() => {
                notification.show("Groups added!");
            });
    };

    const requestAddGroups = () => {
        modal.show({
            title: "Add Groups",
            confirmText: "Add groups",
            children: (
                <Formik
                    initialValues={{ roles: [] }}
                    onSubmit={addGroups}
                    innerRef={formRef}
                >
                    <Form>
                        <AutocompleteInput
                            label="Groups"
                            id="roles"
                            name="roles"
                            onRequestSearch={async (term) => {
                                let items = await oauthGet("/api/v2/role", {
                                    keyword: term,
                                    license: auth.publication.uuid,
                                });
                                return roleTransformer(items).map((item) => {
                                    return {
                                        label: item.name,
                                        value: item.id,
                                    };
                                });
                            }}
                        />
                    </Form>
                </Formik>
            ),
            onRequestClose: (next) => {
                modal.set({ confirmText: "Saving..." });
                formRef.current.submitForm().then((data) => {
                    next();
                });
            },
        });
    };

    useEffect(() => {
        if (props.showGroupInvite === true) {
            requestAddGroups();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.showGroupInvite]);

    return (
        <div className="space-y-1">
            <Text label="Groups" />
            <p className="text-xs text-gray-500">
                Viewing groups for the current publication, only.
            </p>
            <table className="min-w-full divide-y divide-gray-300">
                <thead>
                    <tr>
                        <th
                            scope="col"
                            className="py-3.5 pl-4 pr-3 text-left text-xs font-normal text-gray-700 sm:pl-6 md:pl-0"
                        >
                            Application
                        </th>
                        <th
                            scope="col"
                            className="py-3.5 px-3 text-left text-xs font-normal text-gray-700"
                        >
                            Group
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                    {roles
                        .filter((r) => r.license_id === auth.publication.id)
                        .map((role) => (
                            <tr key={role.id}>
                                <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6 md:pl-0">
                                    {role.application.name}
                                </td>
                                <td className="whitespace-nowrap py-4 px-3 text-sm text-gray-500">
                                    <Link
                                        to={`/app/group/${role.id}`}
                                        className="text-green-600 underline"
                                    >
                                        {role.name}
                                    </Link>
                                </td>
                            </tr>
                        ))}
                </tbody>
            </table>
            <SecondaryButton
                label="Add groups"
                type="button"
                onClick={requestAddGroups}
            />
        </div>
    );
}
